import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { salesService } from '../../services/salesService';
import { contactsService } from '../../services/contactsService';
import { XMarkIcon } from '@heroicons/react/24/outline';

export default function FinalizeSale({ isOpen, onClose, order, onSaleFinalized }) {
  const [loading, setLoading] = useState(false);
  const [customerData, setCustomerData] = useState(null);
  const [saleData, setSaleData] = useState({
    items: [],
    discount: '',
    amountReceived: '',
    remainingBalance: '',
    cratesGiven: '',
    cratesReturned: ''
  });

  // Initialize order data
  useEffect(() => {
    if (order) {
      setSaleData(prev => ({
        ...prev,
        items: order.items.map(item => ({
          ...item,
          actualQuantity: item.estimatedQuantity,
          pricePerKg: '',
        }))
      }));
    }
  }, [order]);

  // Fetch customer data
  useEffect(() => {
    const fetchCustomerData = async () => {
      if (!order?.customerId) return;
      try {
        const customer = await contactsService.getContact(order.customerId);
        setCustomerData(customer);
      } catch (error) {
        console.error('Error fetching customer data:', error);
      }
    };
    fetchCustomerData();
  }, [order?.customerId]);

  const calculateSubtotal = () => {
    return saleData.items.reduce((total, item) => {
      const quantity = Number(item.actualQuantity) || 0;
      const price = Number(item.pricePerKg) || 0;
      return total + (quantity * price);
    }, 0);
  };

  const calculateTotal = () => {
    const subtotal = calculateSubtotal();
    const discount = Number(saleData.discount) || 0;
    return subtotal - discount;
  };

  const handleItemChange = (index, field, value) => {
    if (field === 'actualQuantity' || field === 'pricePerKg') {
      if (value === '' || /^\d*\.?\d*$/.test(value) || value === '.') {
        const newItems = [...saleData.items];
        newItems[index] = { 
          ...newItems[index], 
          [field]: value
        };
        setSaleData(prev => ({ ...prev, items: newItems }));
      }
    }
  };

  const handleDiscountChange = (value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value) || value === '.') {
      setSaleData(prev => ({ 
        ...prev, 
        discount: value
      }));
    }
  };

  const handleNumericChange = (field, value) => {
    if (field === 'amountReceived') {
      if (value === '' || /^\d*\.?\d*$/.test(value) || value === '.') {
        setSaleData(prev => ({ 
          ...prev, 
          [field]: value
        }));
      }
    } else {
      if (value === '' || /^\d*$/.test(value)) {
        setSaleData(prev => ({ 
          ...prev, 
          [field]: value === '' ? '' : Math.max(0, parseInt(value) || 0)
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const totalAmount = calculateTotal();
      const remainingBalance = totalAmount - saleData.amountReceived;
      const netCratesChange = saleData.cratesGiven - saleData.cratesReturned;
      
      await salesService.finalizeSale(order.id, {
        ...saleData,
        totalAmount,
        remainingBalance,
        subtotal: calculateSubtotal(),
        netCratesChange
      });
      onSaleFinalized();
      onClose();
    } catch (error) {
      console.error('Error finalizing sale:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!order) {
    return null; // Or return a loading state if preferred
  }

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full transform overflow-hidden bg-white dark:bg-gray-800 
                p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl
                sm:max-w-3xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Finalize Sale {order?.customerName && `- ${order.customerName}`}
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500"
                    onClick={onClose}
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Customer Info with Crates */}
                  <div className="mb-6 space-y-4">
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                      Customer: {order.customerName}
                    </h3>
                    
                    {customerData && (
                      <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300">
                              Current Boxes with Customer
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {Math.abs(customerData.cratesOut) > 0 
                                ? `Customer has ${Math.abs(customerData.cratesOut)} boxes`
                                : 'No crates with customer'}
                            </p>
                          </div>
                          <div className={`text-2xl font-bold ${
                            Math.abs(customerData.cratesOut) > 0 
                              ? 'text-yellow-600 dark:text-yellow-400' 
                              : 'text-gray-600 dark:text-gray-400'
                          }`}>
                            {Math.abs(customerData.cratesOut)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Items */}
                  <div className="space-y-4">
                    {saleData.items.map((item, index) => (
                      <div key={index} className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                        <div>
                          <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                            {item.vegetable}
                          </label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Actual Quantity (kg)
                              </label>
                              <input
                                type="text"
                                value={item.actualQuantity}
                                onChange={(e) => handleItemChange(index, 'actualQuantity', e.target.value)}
                                className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">
                                Price/kg
                              </label>
                              <input
                                type="text"
                                value={item.pricePerKg}
                                onChange={(e) => handleItemChange(index, 'pricePerKg', e.target.value)}
                                className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                                required
                              />
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-end">
                          <p className="text-xl font-medium dark:text-white">
                            ₹{(item.actualQuantity * item.pricePerKg).toFixed(2)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Crates Management section */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                    <h3 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-4">
                      Boxes Management
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Boxes Given
                        </label>
                        <input
                          type="text"
                          value={saleData.cratesGiven}
                          onChange={(e) => handleNumericChange('cratesGiven', e.target.value)}
                          className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Boxes Returned
                        </label>
                        <input
                          type="text"
                          value={saleData.cratesReturned}
                          onChange={(e) => handleNumericChange('cratesReturned', e.target.value)}
                          className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center text-lg">
                      <p className="text-gray-700 dark:text-gray-300">Net Boxes Change:</p>
                      <p className={`text-xl font-bold ${
                        saleData.cratesGiven - saleData.cratesReturned > 0 
                          ? 'text-yellow-600 dark:text-yellow-400' 
                          : 'text-green-600 dark:text-green-400'
                      }`}>
                        {saleData.cratesGiven - saleData.cratesReturned}
                      </p>
                    </div>
                    
                    {/* Add Expected Final Crates Count */}
                    <div className="flex justify-between items-center text-lg pt-2 border-t border-gray-200 dark:border-gray-700">
                      <p className="text-gray-700 dark:text-gray-300">Expected Final Boxes Count:</p>
                      <p className={`text-xl font-bold ${
                        Math.abs(customerData?.cratesOut || 0) + (saleData.cratesGiven - saleData.cratesReturned) > 0
                          ? 'text-yellow-600 dark:text-yellow-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {customerData ? Math.abs(customerData.cratesOut) + (saleData.cratesGiven - saleData.cratesReturned) : 0}
                      </p>
                    </div>
                  </div>

                  {/* Totals */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <p className="text-lg font-medium text-gray-700 dark:text-gray-300">Subtotal:</p>
                      <p className="text-xl font-medium dark:text-white">₹{calculateSubtotal().toFixed(2)}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Discount Amount
                        </label>
                        <input
                          type="text"
                          value={saleData.discount}
                          onChange={(e) => handleDiscountChange(e.target.value)}
                          className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                        />
                      </div>
                      <div>
                        <label className="block text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Amount Received
                        </label>
                        <input
                          type="text"
                          value={saleData.amountReceived}
                          onChange={(e) => handleNumericChange('amountReceived', e.target.value)}
                          className="w-full px-4 py-3 text-lg rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:ring-primary-500 focus:border-primary-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="flex justify-between items-center text-lg font-medium">
                      <p className="text-gray-700 dark:text-gray-300">Total After Discount:</p>
                      <p className="text-xl font-bold text-primary-600 dark:text-primary-400">
                        ₹{calculateTotal().toFixed(2)}
                      </p>
                    </div>

                    <div className="flex justify-between items-center text-lg font-medium">
                      <p className="text-gray-700 dark:text-gray-300">Balance Amount:</p>
                      <p className="text-xl font-bold text-red-600 dark:text-red-400">
                        ₹{(calculateTotal() - saleData.amountReceived).toFixed(2)}
                      </p>
                    </div>

                    <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                      <button
                        type="button"
                        onClick={onClose}
                        className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 
                          bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                          hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white 
                          bg-primary-600 rounded-lg border border-transparent
                          hover:bg-primary-700 
                          focus:outline-none focus:ring-2 focus:ring-primary-500
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Finalizing...' : 'Finalize Sale'}
                      </button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}